import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loading } from '@components/common';
import styles from './ArticlePage.module.css';

interface KBArticleDetail {
  title: string;
  id: string;
  type: string;
  modifiedDatetime: string;
  modifiedDate: string;
  description?: string;
  content?: string;
  symptoms?: string;
  cause?: string;
  resolution?: string;
}

const ArticlePage: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<KBArticleDetail | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'done'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  console.log('[ArticlePage] Component mounted/updated, articleId:', articleId);

  useEffect(() => {
    async function loadArticle() {
      console.log('[ArticlePage] Loading article:', articleId);
      
      if (!articleId) {
        console.error('[ArticlePage] No articleId provided');
        setStatus('error');
        setErrorMessage('Article ID is required');
        return;
      }

      setStatus('loading');
      setErrorMessage('');

      try {
        // Dynamic URL detection - supports both dev and production
        const hostname = window.location.hostname;
        const isDev = hostname === 'localhost' || hostname === '127.0.0.1';
        
        // Use appropriate base URL based on environment
        const baseUrl = isDev 
          ? (import.meta.env.VITE_APP_URL_DEV || 'http://localhost:3001')
          : (import.meta.env.VITE_APP_URL_PRODUCTION || '');
        
        const apiUrl = `${baseUrl}/api/Knowledge/GetKBArticle?articleId=${encodeURIComponent(articleId)}`;
        console.log('[ArticlePage] Environment:', isDev ? 'Development' : 'Production');
        console.log('[ArticlePage] Base URL:', baseUrl);
        console.log('[ArticlePage] Fetching from:', apiUrl);
        
        const res = await fetch(apiUrl, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('[ArticlePage] Response status:', res.status, res.statusText);

        // Handle redirect from backend (302 or redirect response)
        if (res.status === 302) {
          const location = res.headers.get('Location');
          if (location) {
            // Extract article ID from redirect location
            const newArticleId = location.split('/').pop() || location.split('=').pop();
            if (newArticleId) {
              navigate(`/knowledge/article/${newArticleId}`, { replace: true });
              return;
            }
          }
        }

        // Handle redirect in response body (if backend returns JSON with redirect info)
        if (res.status === 200) {
          const contentType = res.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await res.json();
            if (data.redirect && data.articleId) {
              navigate(`/knowledge/article/${data.articleId}`, { replace: true });
              return;
            }
            // Normal article data
            console.log('[ArticlePage] Article data received:', data);
            setArticle(data);
            setStatus('done');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        }

        if (!res.ok) {
          const errorText = await res.text();
          console.error('[ArticlePage] Error response:', res.status, errorText);
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { msg: `Failed to load article: ${res.statusText}` };
          }
          setErrorMessage(errorData.msg || `Failed to load article: ${res.statusText}`);
          setStatus('error');
          return;
        }

        const data: KBArticleDetail = await res.json();
        console.log('[ArticlePage] Article data received:', data);
        setArticle(data);
        setStatus('done');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.error('[ArticlePage] Error loading KB article:', e);
        setErrorMessage(e instanceof Error ? e.message : 'Failed to load article');
        setStatus('error');
      }
    }

    loadArticle();
  }, [articleId, navigate]);

  if (status === 'loading') {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <Loading />
          <p>Loading article…</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || !article) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <h1>Article not found</h1>
          <p>{errorMessage || 'We could not load this article. It may have been removed or moved.'}</p>
          <div className={styles.errorActions}>
            <Link to="/knowledge/launch" className={styles.backButton}>
              ← Back to Search
            </Link>
            <button onClick={() => navigate(-1)} className={styles.backButton}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { title, type, modifiedDate, description, content, symptoms, cause, resolution } = article;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.articlePage}>
        {/* Breadcrumbs */}
        <nav className={styles.breadcrumbs}>
          <Link to="/knowledge">Knowledge Base</Link>
          <span className={styles.breadcrumbSeparator}> &gt; </span>
          <Link to="/knowledge/launch">Search</Link>
          <span className={styles.breadcrumbSeparator}> &gt; </span>
          <span>{type || 'Article'}</span>
        </nav>

        {/* Header */}
        <header className={styles.articleHeader}>
          <h1 className={styles.articleTitle}>{title}</h1>
          <div className={styles.articleMeta}>
            <span className={styles.typeBadge}>{type}</span>
            {modifiedDate && (
              <span className={styles.modifiedDate}>Last updated: {modifiedDate}</span>
            )}
            {article.id && (
              <span className={styles.articleId}>Article ID: {article.id}</span>
            )}
          </div>
        </header>

        {/* Description */}
        {description && (
          <section className={styles.articleSection}>
            <div
              className={styles.articleDescription}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </section>
        )}

        {/* Symptoms / Cause / Resolution (like Commvault KB) */}
        {(symptoms || cause || resolution) && (
          <div className={styles.articleColumns}>
            {symptoms && (
              <section className={styles.articleSection}>
                <h2>Symptoms</h2>
                <div
                  className={styles.articleContent}
                  dangerouslySetInnerHTML={{ __html: symptoms }}
                />
              </section>
            )}
            {cause && (
              <section className={styles.articleSection}>
                <h2>Cause</h2>
                <div
                  className={styles.articleContent}
                  dangerouslySetInnerHTML={{ __html: cause }}
                />
              </section>
            )}
            {resolution && (
              <section className={styles.articleSection}>
                <h2>Resolution</h2>
                <div
                  className={styles.articleContent}
                  dangerouslySetInnerHTML={{ __html: resolution }}
                />
              </section>
            )}
          </div>
        )}

        {/* Main article content (HTML from Alexandria) */}
        {content && (
          <section className={styles.articleSection}>
            <h2>Details</h2>
            <div
              className={styles.articleContent}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </section>
        )}

        {/* Back to search button */}
        <div className={styles.articleFooter}>
          <Link to="/knowledge/launch" className={styles.backButton}>
            ← Back to Search
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;

